"use client";

import { useEffect, useState } from "react";

import { cx } from "@tripcart/traveler-ui/cx";

import styles from "./_rail-dots.module.css";

/**
 * The guide's `.hero-dots` / `.rvc-dots` — where you are in a rail that scrolls
 * sideways.
 *
 * WHY IT EXISTS AT ALL. A rail on a phone is gestural: no arrows, no scrollbar.
 * The only thing that says "there is more to the right" is the next card
 * peeking past the edge — and that cue disappears the moment a rail's last card
 * is in view, so a reader who lands mid-rail cannot tell whether they are at
 * the start, the middle or the end. The dots are the position, and the guide
 * puts them under every content carousel it draws.
 *
 * ON CONTENT RAILS ONLY. The page has two kinds of horizontal scroller and they
 * must not look alike: content carousels (photos, reviews, tours) and CONTROL
 * rows (month chips, sort chips, source chips, the section tab strip). Dots on
 * a control row would promise pages of content that do not exist — the row is
 * one set of filters that happens to be wider than the screen. The guide gives
 * `.fchips`, `.seg` and `.stabs` no dots for the same reason.
 *
 * HOW IT READS THE POSITION. It listens to the rail's own `scroll` event rather
 * than mounting an IntersectionObserver per card — a rail of six cards would
 * otherwise cost six observers whose only job is to produce one number.
 *
 * The SCROLL HANDLER TOUCHES ONE PROPERTY: `scrollLeft`. The slide width is
 * measured once when the effect runs and again only when a `ResizeObserver`
 * says the rail changed size. That split is the whole point, and it was wrong
 * in the first version of this file: the handler read `firstElementChild`,
 * `lastElementChild`, `offsetLeft` and `clientWidth` on every event, and
 * `offsetLeft`/`clientWidth` each force a synchronous style-and-layout flush.
 * A phone flinging the hero carousel fired that at the scroll event rate, which
 * is exactly the per-frame main-thread work this app's INP budget forbids — and
 * the comment here claimed it cost "one division and one compare per event".
 */

/** How many dots may be drawn before the row stops reading as a position and
 *  starts reading as a texture. The mobile guide's own gallery is twelve and it
 *  draws all twelve, so that is the number: at or below it every slide gets its
 *  own dot and we match the guide exactly. Past it the row WINDOWS (see
 *  `dotWindow`) instead of disappearing — a 20-photo gallery is precisely the
 *  one that most needs to say it scrolls. */
const MAX_DOTS = 12;

/**
 * Which dots to draw, and which of them is current.
 *
 * At or below `MAX_DOTS` this is every index. Above it, a window of `MAX_DOTS`
 * centred on the current slide and clamped to the ends — so the row stays a
 * fixed width, the active dot sits in the middle while you are in the middle,
 * and it reaches the edge when you do.
 */
export function dotWindow(
  total: number,
  index: number,
): { indices: number[]; hasBefore: boolean; hasAfter: boolean } {
  if (total <= MAX_DOTS) {
    return {
      indices: Array.from({ length: total }, (_, i) => i),
      hasBefore: false,
      hasAfter: false,
    };
  }
  const half = Math.floor(MAX_DOTS / 2);
  const start = Math.min(Math.max(0, index - half), total - MAX_DOTS);
  return {
    indices: Array.from({ length: MAX_DOTS }, (_, i) => start + i),
    hasBefore: start > 0,
    hasAfter: start + MAX_DOTS < total,
  };
}

/**
 * Which slide a rail is showing, from its scroll offset.
 *
 * `scrollLeft / clientWidth` for a full-width rail (the hero), and the caller
 * passes `slideWidth` for a rail whose cards are narrower than the track. The
 * rail is looked up BY ID rather than by ref because most of these tracks are
 * server-rendered — a ref cannot cross that boundary, and lifting a whole
 * gallery to the client to share one would put its images behind hydration.
 */
export function useRailIndex(trackId: string, total: number): number {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const track = document.getElementById(trackId);
    if (!track) return;

    // One slide's advance, in pixels. Measured from the DOM rather than passed
    // in, because the rails size their cards in percentages (`w-[84%]`) and any
    // constant would be wrong at some screen width.
    let step = 1;
    const measure = () => {
      const first = track.firstElementChild as HTMLElement | null;
      const last = track.lastElementChild as HTMLElement | null;
      // The DIFFERENCE between the two, not `last.offsetLeft` alone. Both are
      // measured from the nearest positioned ancestor, which for a padded rail
      // is not the rail — so `last.offsetLeft` carries the rail's own left
      // padding (and whatever the ancestor adds) and inflated every step. The
      // subtraction cancels the shared origin whatever it happens to be.
      step =
        first && last && first !== last
          ? Math.max(1, (last.offsetLeft - first.offsetLeft) / (total - 1))
          : Math.max(1, track.clientWidth);
    };
    const read = () => {
      const next = Math.min(
        total - 1,
        Math.max(0, Math.round(track.scrollLeft / step)),
      );
      setIndex((current) => (current === next ? current : next));
    };

    measure();
    read();
    // Re-measure only when the rail actually changes size — a rotation, a
    // browser resize, or a font swap that reflows the cards.
    const resize = new ResizeObserver(() => {
      measure();
      read();
    });
    resize.observe(track);
    track.addEventListener("scroll", read, { passive: true });
    return () => {
      resize.disconnect();
      track.removeEventListener("scroll", read);
    };
  }, [trackId, total]);

  return index;
}

export interface RailDotsProps {
  /** `id` of the scrolling track. */
  trackId: string;
  total: number;
  /**
   * `light` for dots over a photo (the hero), `brand` for dots on the page
   * background (reviews, similar tours). The guide uses white-on-white for the
   * first and `--brand` on `--border-md` for the second — the same control in
   * two places that need two different contrasts against what is behind them.
   */
  tone?: "light" | "brand";
  className?: string;
}

/**
 * `aria-hidden` throughout, and that is deliberate rather than an oversight.
 *
 * The dots duplicate information a screen-reader user already has better: the
 * rail is a list, and its reader announces "3 of 12" while moving through it.
 * Announcing a second, redundant position — one that cannot be operated,
 * because these are not buttons — would be noise. The hero's visible "1 / 20"
 * counter IS focusable and IS named, so the count is never only in the dots.
 */
export function RailDots({
  trackId,
  total,
  tone = "brand",
  className,
}: RailDotsProps) {
  const index = useRailIndex(trackId, total);
  if (total <= 1) return null;

  const { indices, hasBefore, hasAfter } = dotWindow(total, index);

  return (
    <div aria-hidden className={cx(styles.dots, className)} data-tone={tone}>
      {indices.map((i) => (
        <span
          key={i}
          className={styles.dot}
          data-active={i === index ? "" : undefined}
          // The window's edge dots shrink when there is more beyond them —
          // the row's way of saying it is a window rather than the whole
          // gallery. Without this a 40-photo rail looks like a 12-photo one
          // whose dots simply never reach the end.
          data-edge={
            (hasBefore && i === indices[0]) ||
            (hasAfter && i === indices[indices.length - 1])
              ? ""
              : undefined
          }
        />
      ))}
    </div>
  );
}
